import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import {
  AlertType,
  ICustomerNoteMutationResult,
  ICustomerNoteMutationVariables,
} from '../../../graphql/schema/p4types';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../../utils/getClientMutationId';
import { serviceActions } from '../../../state/modules/service';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Text,
  Textarea,
} from '@fluentui/react-components';
import { DangerButton, SecondaryButton } from '../Button';
import { IP4Note, MemoContext, useNoteConfig } from './MemoEditor';

export const MemoDelete = ({ note }: { note: IP4Note }) => {
  const [open, setOpen] = React.useState(false);
  const { resetList, type } = useContext(MemoContext);
  const { mutationNode, mutationKey, refetchNodes } = useNoteConfig(type);
  const dispatch = useDispatch();

  const handleQueryError = useComponentQueryErrorHandling();
  const [saveNote] = useMutation<ICustomerNoteMutationResult, ICustomerNoteMutationVariables>(mutationNode, {
    onError: handleQueryError,
    refetchQueries: refetchNodes,
  });

  const onClickDelete = () => {
    //For notes that support note types, we need to initially the note type onto the record here.
    const deletedNote = { ...normalizeVariables(note), deleted: true };

    saveNote({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          [mutationKey]: deletedNote,
        },
      },
    }).then(() => {
      setOpen(false);
      resetList?.();
      dispatch(serviceActions.genericAlert('Note Save', 'Note saved successfully', AlertType.Success));
    });
  };

  return (
    <Dialog open={open} onOpenChange={(event, data) => setOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        <DangerButton data-testid={'memo-open-delete-note-modal'} small={true}>
          Delete
        </DangerButton>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete Note</DialogTitle>
          <DialogContent>
            <Text>Are you sure you want to delete this note?</Text>
            <Textarea
              style={{ width: '100%', height: '150px', paddingTop: '5px' }}
              value={note.text ?? ''}
              readOnly={true}
              data-testid={'memo-delete-note-text-area'}
            />
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <SecondaryButton data-testid={'memo-cancel-delete-note'}>Cancel</SecondaryButton>
            </DialogTrigger>
            <DangerButton data-testid={'memo-confirm-delete-note'} onClick={onClickDelete}>
              Delete
            </DangerButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
